import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { assetUrl } from "@/lib/api";
import { LogoMark } from "./Logo";

export const DIVISION_META = {
  "documentaries": { title: "Documentaries", desc: "Long-form truth. Verified stories from the world of the Chronicles." },
  "short-films": { title: "Short Films", desc: "Narrative short films inspired by approved chronicles." },
  "feature-films": { title: "Feature Films", desc: "Full-length motion pictures. Announced when confirmed." },
  "original-series": { title: "Original Series", desc: "Episodic productions, season by season." },
  "trailers": { title: "Trailers", desc: "Cinematic trailers and teasers." },
  "reels": { title: "Reels", desc: "Short-form promotional films." },
  "premieres": { title: "Premieres", desc: "Upcoming releases with confirmed dates." },
  "behind-the-scenes": { title: "Behind the Scenes", desc: "Production features and author conversations." },
};

export function PosterTile({ video, vertical = false }) {
  if (video.poster_url) {
    return <img src={assetUrl(video.poster_url)} alt={`Poster for ${video.title}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" loading="lazy" />;
  }
  return (
    <div className="w-full h-full bg-gradient-to-b from-[#141414] to-[#080808] flex flex-col items-center justify-center gap-4 p-6 text-center">
      <LogoMark size={40} />
      <p className="font-display text-paper/80 text-base leading-snug">{video.title}</p>
      <span className="w-8 h-px bg-crimson" />
    </div>
  );
}

export default function PosterCard({ video, vertical = false }) {
  return (
    <Link to={`/cinema/watch/${video.slug}`} data-testid={`poster-${video.slug}`}
      className={`group block shrink-0 border border-paper/10 bg-coal overflow-hidden transition-colors duration-500 hover:border-crimson/60 ${vertical ? "w-44 sm:w-52" : "w-64 sm:w-72"}`}>
      <div className={`${vertical ? "aspect-[9/16]" : "aspect-video"} overflow-hidden relative bg-black`}>
        <PosterTile video={video} vertical={vertical} />
        {video.runtime && (
          <span className="absolute bottom-2 right-2 font-ui text-[10px] bg-ink/85 text-paper px-2 py-1 rounded">
            {video.runtime}
          </span>
        )}
      </div>
      <div className="p-4">
        <p className="font-display text-sm text-paper leading-snug truncate">{video.title}</p>
        <p className="font-ui text-[10px] uppercase tracking-[0.2em] text-paper/40 mt-1.5">
          {DIVISION_META[video.division]?.title || video.division}
        </p>
      </div>
    </Link>
  );
}

export function Countdown({ releaseAt, timezone }) {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    const target = new Date(releaseAt).getTime();
    const tick = () => {
      const d = target - Date.now();
      if (d <= 0) {
        setLeft(null);
        return;
      }
      setLeft({
        d: Math.floor(d / 86400000),
        h: Math.floor((d % 86400000) / 3600000),
        m: Math.floor((d % 3600000) / 60000),
        s: Math.floor((d % 60000) / 1000),
      });
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [releaseAt]);

  if (!releaseAt) return null;
  if (!left) {
    return <p className="font-display text-gold text-lg tracking-wide" data-testid="countdown-live">Premiering now</p>;
  }
  return (
    <div data-testid="premiere-countdown">
      <div className="flex gap-4 sm:gap-6">
        {[["Days", left.d], ["Hours", left.h], ["Min", left.m], ["Sec", left.s]].map(([label, val]) => (
          <div key={label} className="text-center">
            <p className="font-display font-black text-3xl sm:text-5xl text-paper tabular-nums">{String(val).padStart(2, "0")}</p>
            <p className="font-ui text-[9px] uppercase tracking-[0.3em] text-paper/45 mt-1">{label}</p>
          </div>
        ))}
      </div>
      <p className="font-ui text-xs text-paper/45 mt-4 inline-flex items-center gap-2">
        <Clock size={13} />
        {new Date(releaseAt).toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" })}
        {timezone ? ` (${timezone})` : ""}
      </p>
    </div>
  );
}

export function VideoRow({ title, videos, vertical = false, testid }) {
  if (!videos?.length) return null;
  return (
    <section className="py-10" data-testid={testid}>
      <h2 className="font-display font-bold text-2xl sm:text-3xl text-paper mb-6 px-6 sm:px-10 max-w-7xl mx-auto">{title}</h2>
      <div className="flex gap-5 overflow-x-auto px-6 sm:px-10 pb-4 max-w-7xl mx-auto" style={{ scrollbarWidth: "thin" }}>
        {videos.map((v) => <PosterCard key={v.video_id} video={v} vertical={vertical} />)}
      </div>
    </section>
  );
}
